import React, { useState, useEffect, useCallback, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import cn from 'classnames'
import { Dropin, Field, PaypalButton, DropinContext } from 'braintreeact'
import { AudioContext } from 'rewact'
import { Loader as LoaderIcon } from '../Icons'
import { makePayment } from '../../api'
import { Button } from '../controls'
import LogoPixedenMusic from '../LogoPixedenMusic'
import { useQuery } from '@apollo/client'
import { QUERY } from '../../apollo'
import TrackContext from '../../context/TrackContext'
import { useDelayedState } from '../../hooks'
import * as Icon from '../Icons'

import styles from './payment.module.css'
import mStyles from './modal.module.css'

const hfStyles = {
  input: {
    'font-size': '18px',
    'font-family': 'Avenir',
    'font-weight': '400',
    color: '#6e6e6f',
  },
  label: {
    height: '30px',
  },
  ':focus': {
    color: '#6e6e6f',
  },
  '.valid': {
    color: '#09090f',
  },
  '.invalid': {
    color: 'var(--c-red, #ff3b57)',
  },
}

const PayPalMethod = ({ payload, reset }) => {
  return (
    <div className={styles.paypalMethod}>
      <img
        src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png"
        alt="PayPal Logo"
      />
      {payload.details.email}
      <Button onClick={reset} className={styles.paypalMethodReset}>
        Reset
      </Button>
    </div>
  )
}

function PaypalCustomButton() {
  const { paypalReady, paypalPayload, reset } = useContext(DropinContext)
  return (
    <div
      style={{
        width: paypalPayload ? '100%' : 150,
        marginBottom: 50,
        opacity: paypalReady ? 1 : 0.5,
        pointerEvents: paypalReady ? 'auto' : 'none',
      }}
    >
      {paypalPayload ? (
        <PayPalMethod payload={paypalPayload} reset={reset} />
      ) : (
        <PaypalButton />
      )}
    </div>
  )
}

function CustomFields() {
  const { paypalPayload } = useContext(DropinContext)
  const [focused, setFocused] = useState({
    number: false,
    expirationDate: false,
    cvv: false,
  })

  const [fieldsEmpty, setFieldsEmpty] = useState({
    number: true,
    expirationDate: true,
    cvv: true,
  })

  const onFocus = ({ emittedBy }) => {
    setFocused((f) => ({ ...f, [emittedBy]: true }))
  }

  const onBlur = ({ emittedBy, fields }) => {
    setFocused((f) => ({ ...f, [emittedBy]: false }))
    setFieldsEmpty((f) => ({
      ...f,
      [emittedBy]: fields[emittedBy].isEmpty,
    }))
  }

  return paypalPayload ? null : (
    <>
      <label className={styles.fieldContainer}>
        <div
          className={cn(styles.label, {
            [styles.focused]: focused.number || !fieldsEmpty.number,
          })}
        >
          Card Number
        </div>
        <Field
          type="number"
          onFocus={onFocus}
          onBlur={onBlur}
          className={styles.field}
        />
      </label>
      <label className={styles.fieldContainer}>
        <div
          className={cn(styles.label, {
            [styles.focused]: focused.cvv || !fieldsEmpty.cvv,
          })}
        >
          CVV
        </div>
        <Field
          type="cvv"
          onFocus={onFocus}
          onBlur={onBlur}
          className={styles.field}
        />
      </label>
      <label className={styles.fieldContainer}>
        <div
          className={cn(styles.label, {
            [styles.focused]:
              focused.expirationDate || !fieldsEmpty.expirationDate,
          })}
        >
          Expiration Date
        </div>
        <Field
          type="expirationDate"
          onFocus={onFocus}
          onBlur={onBlur}
          className={styles.field}
        />
      </label>
    </>
  )
}

function PaymentModal({
  closeModal,
  setDisabled,
  amount,
  requestType,
  requestId,
  ...track
}) {
  const { artist, name, image, slug, id } = track
  const history = useHistory()
  const [formState, setFormState] = useState({
    response: false,
    error: false,
    errorMessage: '',
    loading: false,
  })

  const { setPlayerTrack, playerTrack } = useContext(TrackContext)
  const { play, isPaused, pause, time, duration } = useContext(AudioContext)
  const setDisabledCB = useCallback(setDisabled, [setDisabled])
  useEffect(() => {
    setDisabledCB(formState.loading)
  }, [formState.loading, setDisabledCB])

  const { data, refetch } = useQuery(QUERY.USER_INFO)
  const userInfo = data?.userInfo

  const [isReallyPaused] = useDelayedState(
    isPaused,
    process.env.NODE_ENV === 'production' ? 300 : 100
  )
  const m = '0' + Math.round(((duration - time) / 60) % 60)
  const s = '0' + Math.round((duration - time) % 60)

  const active = !isReallyPaused && slug === playerTrack.slug
  return (
    <div className={mStyles.wrapper}>
      <div className={mStyles.containerOut}>
        <LogoPixedenMusic className={mStyles.logoPixedenMusic} />
        <div className={styles.contentWrapper}>
          <div className={mStyles.sidebar}>
            <p className={mStyles.textBig}>
              Master and Publishing sync rights license purchase.
            </p>
            <div
              className={mStyles.playerItem}
              title={`"${name}" by ${artist}`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!active) {
                  setPlayerTrack({
                    id,
                    artist,
                    name,
                    slug,
                    image,
                  })
                  setTimeout(() => play(), 100)
                } else {
                  if (isReallyPaused) {
                    play()
                  } else {
                    pause()
                  }
                }
              }}
            >
              {image ? (
                <img
                  alt="Cover"
                  className={mStyles.playerItemImg}
                  src={
                    image.startsWith('/')
                      ? image
                      : `${process.env.REACT_APP_COVER_URI}/m/${image}`
                  }
                />
              ) : null}
              <div className={mStyles.playerItemDetails}>
                <h3 className={mStyles.playerItemArtist}>{artist}</h3>
                <p className={mStyles.playerItemName}>{name}</p>
              </div>
              {active && (
                <div className={mStyles.playerItemTime}>
                  {m.substr(-2)}:{s.substr(-2)}
                </div>
              )}
              <div
                className={cn(mStyles.playerItemBtn, {
                  [mStyles.visible]: active,
                })}
              >
                {active ? (
                  <Icon.Pause />
                ) : (
                  <Icon.Play style={{ marginLeft: 2 }} />
                )}
              </div>
            </div>
          </div>
          <div className={mStyles.mainSide}>
            <p className={mStyles.textSmall}>
              For payment with Wire bank account transfer, please request
              payment information to music@pixeden.com or contact us through our
              messaging system.
            </p>
            {formState.loading && (
              <p className={styles.text}>
                <LoaderIcon className={styles.loaderIcon} /> Processing Payment…
              </p>
            )}
            {formState.response ? (
              <p
                className={mStyles.text}
                style={{ color: 'var(--c-red', fontSize: '1.7em' }}
              >
                License purchased successfully
              </p>
            ) : (
              <>
                <Dropin
                  authorization={process.env.REACT_APP_BT_TOKEN}
                  paypalStyles={{
                    color: 'black',
                    layout: 'horizontal',
                    tagline: false,
                    shape: 'pill',
                  }}
                  styles={hfStyles}
                  withPaypal
                >
                  <div
                    className={cn(styles.formWrapper, {
                      [styles.formDisabled]: formState.loading,
                    })}
                  >
                    <div className="container">
                      <PaypalCustomButton />
                      <CustomFields />
                    </div>
                  </div>
                  <PurchaseButton
                    onClick={async ({ getPayload }) => {
                      try {
                        setFormState({
                          response: false,
                          error: false,
                          errorMessage: '',
                          loading: true,
                        })
                        const { nonce } = await getPayload()

                        const info = await makePayment({
                          amount,
                          artist,
                          name,
                          userId: userInfo.id,
                          requestType,
                          requestId,
                          nonce,
                        })

                        setFormState({
                          response: true,
                          error: false,
                          errorMessage: '',
                          loading: false,
                        })

                        await refetch()
                        setTimeout(() => {
                          history.push(`/profile/purchased/${info.purchasedId}`)
                          closeModal()
                        }, 3000)
                      } catch (err) {
                        console.log({ err })
                        setFormState({
                          response: false,
                          error: true,
                          errorMessage: Array.isArray(err)
                            ? `${err
                                .map(mapErrorFields)
                                .join(', ')} are required `
                            : err.message,
                          loading: false,
                        })
                      }
                    }}
                    disabled={formState.loading}
                  >
                    Buy License ${amount}
                  </PurchaseButton>
                  {formState.error ? (
                    formState.errorMessage ? (
                      <p
                        className={styles.text}
                        style={{ color: 'var(--c-red', fontSize: '.76em' }}
                      >
                        {formState.errorMessage}
                      </p>
                    ) : (
                      <p
                        className={styles.text}
                        style={{ color: 'var(--c-red', fontSize: '.76em' }}
                      >
                        Something went wrong
                      </p>
                    )
                  ) : null}
                </Dropin>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function PurchaseButton({ onClick, disabled, children }) {
  const { getPayload } = useContext(DropinContext)
  return (
    <>
      <Button
        className={mStyles.button}
        style={{ marginTop: '1em' }}
        onClick={async (e) => {
          e.preventDefault()
          return await onClick({ getPayload })
        }}
        disabled={disabled}
      >
        {children}
      </Button>
    </>
  )
}

export default PaymentModal

function mapErrorFields(field) {
  if (field === 'number') {
    return 'Card Number'
  }
  if (field === 'cvv') {
    return 'CVV Number'
  }
  if (field === 'expirationDate') {
    return 'Expiration Date'
  }
}
